import type { BrokerFeeWorksheet, WorksheetStep, WorksheetStepId } from "@qw/qw-common";
import { Badge, CollapsibleContent, cn, Icon, Tooltip, TooltipContent, TooltipTrigger } from "@qw/ui";
import type { FC } from "react";

import {
  ChatMessageToolInvocation,
  ChatMessageToolInvocationHeader,
  ChatMessageToolInvocationTitle,
  ChatMessageToolInvocationToggleButton,
} from "./chat-message-tool-invocation";

export interface ComputeBrokerFeeToolInvocationResultProps {
  structuredContent: unknown;
  toolDescription: string;
  toolName: string;
}

/**
 * Renders the structured output of the ComputeBrokerFee MCP tool as a worksheet:
 * a headline result, a step-by-step trace of the algorithm, and an indicator of
 * which rule pinned the final value.
 */
export const ComputeBrokerFeeToolInvocationResult: FC<ComputeBrokerFeeToolInvocationResultProps> = ({
  structuredContent,
  toolDescription,
  toolName,
}) => {
  const worksheet = isBrokerFeeWorksheet(structuredContent) ? structuredContent : null;

  if (!worksheet) {
    // Defensive: shape didn't parse — fall back to a tiny error indicator.
    return (
      <ChatMessageToolInvocation>
        <ChatMessageToolInvocationHeader>
          <Icon icon="error" className="text-destructive" />
          <ChatMessageToolInvocationTitle tooltip={toolName}>
            {toolDescription} (unexpected response shape)
          </ChatMessageToolInvocationTitle>
        </ChatMessageToolInvocationHeader>
      </ChatMessageToolInvocation>
    );
  }

  const { finalFee, dealYield, yieldTargetMet, pinnedBy, steps, inputs } = worksheet;

  return (
    <ChatMessageToolInvocation>
      <ChatMessageToolInvocationHeader>
        <Icon icon="check" className="text-foreground" />
        <ChatMessageToolInvocationTitle tooltip={toolName}>{toolDescription}</ChatMessageToolInvocationTitle>
        <ChatMessageToolInvocationToggleButton />
      </ChatMessageToolInvocationHeader>

      {/* Always-visible headline + inputs strip. The step trace below is collapsible. */}
      <div className="border border-input rounded-lg bg-popover mt-2 overflow-hidden -mx-2 2xl:-mx-1">
        <div className="px-3 py-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-input">
          <div className="text-sm font-semibold text-foreground">{formatMoney(finalFee)}</div>
          <div className="text-xs text-muted-foreground">
            Yield {dealYield.toFixed(2)}%
            <Badge variant={yieldTargetMet ? "green" : "amber"} size="2xs" type="square" className="ml-1.5">
              {yieldTargetMet ? "target met" : "below target"}
            </Badge>
          </div>
          {pinnedBy && (
            <div className="text-xs text-muted-foreground">
              Pinned by{" "}
              <Badge variant="outline" size="2xs" type="square">
                {stepLabel(pinnedBy)}
              </Badge>
            </div>
          )}
        </div>

        <div className="px-3 py-2 flex flex-wrap gap-1.5 text-2xs text-muted-foreground">
          <span>
            <span className="font-semibold">Premium:</span> {formatMoney(inputs.premium)}
          </span>
          <span>·</span>
          <span>
            <span className="font-semibold">Gross:</span> {formatPercent(inputs.grossCommission)}
          </span>
          <span>·</span>
          <span>
            <span className="font-semibold">Agent:</span> {formatPercent(inputs.agentCommission)}
          </span>
          <span>·</span>
          <span>
            <span className="font-semibold">Market:</span> {inputs.marketType}
          </span>
          <span>·</span>
          <span>
            <span className="font-semibold">State:</span> {inputs.homeState}
          </span>
          {inputs.brokerRequestedFee !== undefined && (
            <>
              <span>·</span>
              <span>
                <span className="font-semibold">Requested:</span> {formatMoney(inputs.brokerRequestedFee)}
              </span>
            </>
          )}
        </div>

        <CollapsibleContent className="border-t border-input">
          <ol className="divide-y divide-input">
            {steps.map((step, idx) => (
              <BrokerFeeWorksheetStep key={`${step.id}-${idx}`} step={step} isPinned={step.id === pinnedBy} />
            ))}
          </ol>
        </CollapsibleContent>
      </div>
    </ChatMessageToolInvocation>
  );
};

const BrokerFeeWorksheetStep: FC<{ step: WorksheetStep; isPinned: boolean }> = ({ step, isPinned }) => {
  return (
    <li
      className={cn(
        "px-3 py-2 grid grid-cols-[auto_1fr_auto] gap-x-3 items-baseline text-xs",
        !step.applied && "text-muted-foreground/60",
        isPinned && "bg-amber-50 dark:bg-amber-950/30"
      )}
    >
      <div className="flex items-center gap-1.5">
        <Tooltip>
          <TooltipTrigger>
            <Icon
              icon={step.applied ? "arrow_forward" : "remove"}
              className={cn("text-muted-foreground", step.applied && "text-foreground")}
            />
          </TooltipTrigger>
          <TooltipContent>
            {step.applied ? "Step changed the running fee" : "Step did not change the fee"}
          </TooltipContent>
        </Tooltip>
        <span className="font-mono font-semibold">{stepLabel(step.id)}</span>
      </div>
      <div className="font-mono text-muted-foreground break-words">{step.rule.expression}</div>
      <div className="font-mono text-foreground whitespace-nowrap">
        {formatMoney(step.feeAfter)}
        {step.applied && step.feeBefore !== step.feeAfter && (
          <span className="text-muted-foreground"> (was {formatMoney(step.feeBefore)})</span>
        )}
      </div>
    </li>
  );
};

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

const STEP_LABELS: Record<WorksheetStepId, string> = {
  "no-broker-fee-state": "State prohibits broker fee",
  "no-commission": "No commission",
  "broker-request": "Broker request",
  "yield-target": "Yield target",
  "default-fallback": "Default fallback",
  "round-to-nearest": "Round to nearest",
  "general-min": "General min",
  "general-max": "General max",
  "state-cap-ct": "Connecticut cap",
  "state-cap-mt": "Montana cap",
};

function stepLabel(id: WorksheetStepId | string | null | undefined): string {
  if (!id) {
    return "—";
  }
  return STEP_LABELS[id as WorksheetStepId] ?? id;
}

function formatMoney(value: number): string {
  return value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "—";
  }
  return `${(value * 100).toFixed(2)}%`;
}

// ---------------------------------------------------------------------------
// Shape guard
// ---------------------------------------------------------------------------

function isBrokerFeeWorksheet(value: unknown): value is BrokerFeeWorksheet {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    typeof v.finalFee === "number" &&
    typeof v.dealYield === "number" &&
    typeof v.yieldTargetMet === "boolean" &&
    Array.isArray(v.steps) &&
    typeof v.inputs === "object" &&
    v.inputs !== null
  );
}
